import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * PWA install prompt.
 *
 * Browsers fire `beforeinstallprompt` when the site is eligible to be
 * installed to the home screen (Chrome/Edge on Android + desktop, plus
 * recent Samsung Internet). Safari and Firefox don't fire it, so this
 * banner is silently a no-op there — those users can still "Add to
 * Home Screen" from the browser menu.
 *
 * UX rules:
 *  - Only show after 2 logged-in visits (LOCALSTORAGE_VISIT_COUNT_KEY),
 *    so first-time visitors aren't pestered.
 *  - Dismissable, and a dismiss persists for 30 days
 *    (LOCALSTORAGE_DISMISSED_KEY).
 *  - Never show on the marketing/landing pages (only after login).
 */

const LOCALSTORAGE_VISIT_COUNT_KEY = "__pwa_visit_count__";
const LOCALSTORAGE_DISMISSED_KEY = "__pwa_dismissed_until__";
const MIN_VISITS_BEFORE_PROMPT = 2;
const DISMISS_FOR_DAYS = 30;

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Don't show if already installed (running as standalone app)
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone === true) return; // iOS Safari

    // Honor recent dismissal
    try {
      const dismissedUntil = Number(localStorage.getItem(LOCALSTORAGE_DISMISSED_KEY) || 0);
      if (dismissedUntil && Date.now() < dismissedUntil) return;
    } catch { /* localStorage unavailable, continue */ }

    // Bump visit count
    let visits = 1;
    try {
      visits = Number(localStorage.getItem(LOCALSTORAGE_VISIT_COUNT_KEY) || 0) + 1;
      localStorage.setItem(LOCALSTORAGE_VISIT_COUNT_KEY, String(visits));
    } catch { /* ignore */ }

    const handler = (e) => {
      e.preventDefault(); // Stop the browser's own auto-prompt
      setDeferredPrompt(e);
      if (visits >= MIN_VISITS_BEFORE_PROMPT) setShow(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    // Whatever the user picked, dismiss the banner — Chrome only fires
    // beforeinstallprompt once per session anyway.
    setDeferredPrompt(null);
    setShow(false);
    if (outcome === "dismissed") {
      // Remember dismissal for 30 days
      try {
        localStorage.setItem(
          LOCALSTORAGE_DISMISSED_KEY,
          String(Date.now() + DISMISS_FOR_DAYS * 24 * 60 * 60 * 1000)
        );
      } catch { /* ignore */ }
    }
  };

  const handleDismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(
        LOCALSTORAGE_DISMISSED_KEY,
        String(Date.now() + DISMISS_FOR_DAYS * 24 * 60 * 60 * 1000)
      );
    } catch { /* ignore */ }
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="pwa-install-title"
      className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-700 bg-[#1a1f2e] p-4 shadow-2xl"
    >
      <button
        onClick={handleDismiss}
        aria-label="Dismiss install prompt"
        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-cyan-500/20 p-2">
          <Download className="h-5 w-5 text-cyan-400" />
        </div>
        <div className="flex-1">
          <h3 id="pwa-install-title" className="text-sm font-semibold text-white">
            Install Stacked
          </h3>
          <p className="mt-1 text-xs text-gray-400">
            Get a home-screen icon and a faster, app-like experience.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={handleInstall}
              className="rounded-md bg-cyan-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600"
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="rounded-md border border-gray-700 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
